import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const API = 'https://legado-digital-production.up.railway.app'

function Mensajes({ userId, tipo }) {
  const esCartas = tipo === 'cartas'
  const [mensajes, setMensajes] = useState([])
  const [cargando, setCargando] = useState(true)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const [mensaje, setMensaje] = useState('')
  const [titulo, setTitulo] = useState('')
  const [contenido, setContenido] = useState('')
  const [destinatarioNombre, setDestinatarioNombre] = useState('')
  const [destinatarioEmail, setDestinatarioEmail] = useState('')
  const [fechaEntrega, setFechaEntrega] = useState('')
  const [archivo, setArchivo] = useState(null)

  // cartas = postumo, mihistoria = capsula (reutilizamos los tipos del backend)
  const tipoBackend = esCartas ? 'postumo' : 'capsula'

  useEffect(() => {
    cargarMensajes()
    setTitulo(''); setContenido(''); setDestinatarioNombre('')
    setDestinatarioEmail(''); setFechaEntrega(''); setArchivo(null)
    setError(''); setMensaje('')
  }, [userId, tipo])

  async function cargarMensajes() {
    setCargando(true)
    try {
      const res = await fetch(`${API}/api/messages?userId=${userId}`)
      const datos = await res.json()
      if (datos.ok) {
        const filtrados = datos.datos.filter(m => m.tipo === tipoBackend)
        const mensajesConUrls = await Promise.all(
          filtrados.map(async (m) => {
            if (m.archivo_url && m.archivo_url.includes('mensajes-archivos')) {
              const path = m.archivo_url.split('mensajes-archivos/')[1]?.split('?')[0]
              if (path) {
                const { data: signed } = await supabase.storage
                  .from('mensajes-archivos')
                  .createSignedUrl(path, 60 * 60 * 24 * 365)
                return { ...m, archivo_url: signed?.signedUrl || m.archivo_url }
              }
            }
            return m
          })
        )
        setMensajes(mensajesConUrls)
      }
    } catch {
      setError('No se pudo conectar con el servidor')
    } finally {
      setCargando(false)
    }
  }

  async function subirArchivo() {
    if (!archivo) return { url: null, nombre: null }
    const ext = archivo.name.split('.').pop()
    const nombreArchivo = `${userId}/${Date.now()}.${ext}`
    const { error } = await supabase.storage
      .from('mensajes-archivos')
      .upload(nombreArchivo, archivo, { upsert: true })
    if (error) throw new Error('Error al subir archivo')
    const { data: urlData } = await supabase.storage
      .from('mensajes-archivos')
      .createSignedUrl(nombreArchivo, 60 * 60 * 24 * 365)
    return { url: urlData.signedUrl, nombre: archivo.name }
  }

  async function handleEnviar(e) {
    e.preventDefault()
    setEnviando(true)
    setError('')
    try {
      const { url: archivoUrl, nombre: archivoNombre } = await subirArchivo()
      const res = await fetch(`${API}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          tipo: tipoBackend,
          titulo,
          contenido,
          destinatarioNombre,
          destinatarioEmail,
          fechaEntrega: !esCartas ? fechaEntrega : null,
          archivoUrl,
          archivoNombre,
        }),
      })
      const datos = await res.json()
      if (!datos.ok) { setError(datos.error || 'Error al guardar'); return }
      setMensajes(prev => [datos.datos, ...prev])
      setTitulo(''); setContenido(''); setDestinatarioNombre('')
      setDestinatarioEmail(''); setFechaEntrega(''); setArchivo(null)
      if (document.getElementById('input-archivo')) document.getElementById('input-archivo').value = ''
      setMensaje(esCartas ? 'Carta guardada correctamente.' : 'Historia guardada correctamente.')
      setTimeout(() => setMensaje(''), 4000)
    } catch (err) {
      setError(err.message || 'No se pudo conectar con el servidor')
    } finally {
      setEnviando(false)
    }
  }

  async function handleEliminar(id) {
    await fetch(`${API}/api/messages/${id}`, { method: 'DELETE' })
    setMensajes(prev => prev.filter(m => m.id !== id))
  }

  function formatearFecha(f) {
    if (!f) return ''
    return new Date(f).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
  }

  function iconoArchivo(nombre) {
    if (!nombre) return '📎'
    const ext = nombre.split('.').pop().toLowerCase()
    if (['jpg','jpeg','png','gif','webp'].includes(ext)) return '🖼️'
    if (ext === 'pdf') return '📄'
    if (['mp4','mov','avi'].includes(ext)) return '🎥'
    if (['mp3','m4a','wav'].includes(ext)) return '🎵'
    return '📎'
  }

  return (
    <div style={estilos.pagina} className="pagina-contenido">
      <div style={estilos.contenido}>

        <div style={estilos.header}>
          {esCartas ? (
            <>
              <h2 style={estilos.titulo}>Cartas</h2>
              <p style={estilos.subtitulo}>
  Escribe mensajes personales para quienes quieres — una carta para tu pareja, 
  para tus hijos, para un amigo. Estas cartas se entregarán cuando tu guardián 
  active el acceso a tu información. Para las cosas que quieres que sepan 
  en el momento que más importa.
</p>
            </>
          ) : (
            <>
              <h2 style={estilos.titulo}>Mi historia</h2>
              <p style={estilos.subtitulo}>
                Guarda tus recuerdos, historias de vida, fotos y momentos importantes.
                Elige una fecha futura para que lleguen a quienes elijas —
                un cumpleaños, una graduación, el momento que quieras.
                Para estar presente aunque no puedas estarlo.
              </p>
            </>
          )}
        </div>

        <div style={estilos.descripcion}>
          {esCartas
            ? '💌 Estas cartas llegarán a sus destinatarios cuando tu guardián active el acceso — no antes. Tú decides qué dice cada una y para quién es.'
            : '📅 Estos mensajes se entregarán en la fecha que elijas — un cumpleaños, una graduación, el momento que quieras.'}
        </div>

        <form onSubmit={handleEnviar} style={estilos.formulario}>
          {mensaje && <div style={estilos.mensajeExito}>{mensaje}</div>}
          {error && <div style={estilos.errorDiv}>{error}</div>}

          <div style={estilos.campo}>
            <label style={estilos.label}>{esCartas ? 'Título (opcional)' : 'Título'}</label>
            <input
              type="text"
              value={titulo}
              onChange={e => setTitulo(e.target.value)}
              style={estilos.input}
              placeholder={esCartas ? 'Ej: Para mi hija' : 'Ej: El día de tu boda'}
            />
          </div>

          {esCartas && (
            <div style={estilos.grid}>
              <div style={estilos.campo}>
                <label style={estilos.label}>Para quién es</label>
                <input type="text" value={destinatarioNombre} onChange={e => setDestinatarioNombre(e.target.value)} style={estilos.input} placeholder="Ej: María" />
              </div>
              <div style={estilos.campo}>
                <label style={estilos.label}>Email del destinatario</label>
                <input type="email" value={destinatarioEmail} onChange={e => setDestinatarioEmail(e.target.value)} style={estilos.input} placeholder="maria@email.com" />
              </div>
            </div>
          )}

          {!esCartas && (
            <>
              <div style={estilos.grid}>
                <div style={estilos.campo}>
                  <label style={estilos.label}>Para quién es</label>
                  <input type="text" value={destinatarioNombre} onChange={e => setDestinatarioNombre(e.target.value)} style={estilos.input} placeholder="Ej: María" />
                </div>
                <div style={estilos.campo}>
                  <label style={estilos.label}>Email del destinatario</label>
                  <input type="email" value={destinatarioEmail} onChange={e => setDestinatarioEmail(e.target.value)} style={estilos.input} placeholder="maria@email.com" />
                </div>
              </div>
              <div style={estilos.campo}>
                <label style={estilos.label}>Fecha de entrega</label>
                <input
                  type="date"
                  value={fechaEntrega}
                  onChange={e => setFechaEntrega(e.target.value)}
                  style={estilos.input}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </>
          )}

          <div style={estilos.campo}>
            <label style={estilos.label}>{esCartas ? 'Tu carta' : 'Tu mensaje o historia'}</label>
            <textarea
              value={contenido}
              onChange={e => setContenido(e.target.value)}
              style={{ ...estilos.input, minHeight: '140px', resize: 'vertical' }}
              placeholder={esCartas
                ? 'Escribe lo que quieres que lea...'
                : 'Cuenta tu historia, un recuerdo, un viaje, algo que quieres que sepan de ti...'}
              required
              rows={5}
            />
          </div>

          <div style={estilos.campo}>
            <label style={estilos.label}>Adjuntar archivo (opcional)</label>
            <div style={estilos.archivoZona}>
              <input
                id="input-archivo"
                type="file"
                accept="image/*,.pdf,.mp4,.mov,.mp3,.m4a"
                onChange={e => setArchivo(e.target.files[0] || null)}
                style={{ display: 'none' }}
              />
              <button type="button" onClick={() => document.getElementById('input-archivo').click()} style={estilos.archivoBtn}>
                📎 {archivo ? archivo.name : 'Seleccionar archivo'}
              </button>
              {archivo && (
                <button type="button" onClick={() => { setArchivo(null); document.getElementById('input-archivo').value = '' }} style={estilos.archivoBtnQuitar}>
                  ✕ Quitar
                </button>
              )}
            </div>
            <p style={estilos.archivoHint}>Fotos, PDF, vídeo o audio · Máx. 50MB</p>
          </div>

          <button type="submit" disabled={enviando} style={{ ...estilos.boton, opacity: enviando ? 0.7 : 1 }}>
            {enviando ? 'Guardando...' : esCartas ? 'Guardar carta' : 'Guardar en mi historia'}
          </button>
        </form>

        {cargando && <p style={estilos.vacio}>Cargando...</p>}

        {!cargando && mensajes.length === 0 && (
          <p style={estilos.vacio}>
            {esCartas
              ? 'No tienes cartas todavía. Escribe la primera arriba.'
              : 'Tu historia está vacía. Empieza a añadir recuerdos arriba.'}
          </p>
        )}

        {!cargando && mensajes.map(m => (
          <div key={m.id} style={estilos.card}>
            <div style={estilos.cardHeader}>
              <div>
                {m.titulo && <div style={estilos.cardTitulo}>{m.titulo}</div>}
                <div style={estilos.cardDestinatario}>
                  {m.destinatario_nombre && <span>Para: {m.destinatario_nombre}</span>}
                  {m.destinatario_email && <span> · {m.destinatario_email}</span>}
                </div>
                {m.fecha_entrega && (
                  <div style={estilos.cardFecha}>📅 Se entrega el {formatearFecha(m.fecha_entrega)}</div>
                )}
              </div>
              <button onClick={() => handleEliminar(m.id)} style={estilos.btnEliminar}>✕</button>
            </div>
            <p style={estilos.cardContenido}>{m.contenido}</p>
            {m.archivo_url && (
              <a href={m.archivo_url} target="_blank" rel="noopener noreferrer" style={estilos.archivoLink}>
                {iconoArchivo(m.archivo_nombre)} {m.archivo_nombre || 'Ver archivo adjunto'}
              </a>
            )}
          </div>
        ))}

      </div>
    </div>
  )
}

const estilos = {
  pagina: { minHeight: 'calc(100vh - 64px)', backgroundColor: '#FAF8F5', padding: '32px 16px', scrollMarginTop: '56px' },
  contenido: { maxWidth: '700px', margin: '0 auto' },
  header: { marginBottom: '16px' },
  titulo: { fontSize: '24px', fontWeight: '700', color: '#2C1810', marginBottom: '8px', letterSpacing: '-0.5px' },
  subtitulo: { fontSize: '14px', color: '#8B6F47', lineHeight: '1.7' },
  descripcion: { fontSize: '13px', color: '#8B6F47', backgroundColor: '#F0F4E8', padding: '10px 16px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #C8D5A0' },
  formulario: { backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '24px', border: '1px solid #E8E0D5', marginBottom: '24px' },
  mensajeExito: { backgroundColor: '#F0F4E8', color: '#3D4D1F', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', border: '1px solid #C8D5A0' },
  errorDiv: { backgroundColor: '#FDF0EC', color: '#8B2E0F', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', border: '1px solid #F5C9B8' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  campo: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#2C1810', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #E8E0D5', fontSize: '14px', outline: 'none', backgroundColor: '#FAF8F5', color: '#2C1810', fontFamily: 'inherit' },
  boton: { width: '100%', padding: '13px', backgroundColor: '#2C1810', color: '#FAF8F5', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  archivoZona: { display: 'flex', gap: '8px', alignItems: 'center' },
  archivoBtn: { padding: '9px 16px', borderRadius: '8px', border: '1px solid #E8E0D5', backgroundColor: '#FAF8F5', color: '#2C1810', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' },
  archivoBtnQuitar: { padding: '9px 12px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent', color: '#C4A882', fontSize: '13px', cursor: 'pointer' },
  archivoHint: { fontSize: '11px', color: '#C4A882', marginTop: '6px' },
  vacio: { textAlign: 'center', color: '#C4A882', padding: '40px 24px', backgroundColor: '#FFFFFF', borderRadius: '12px', border: '1px solid #E8E0D5', fontSize: '14px' },
  card: { backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '20px', border: '1px solid #E8E0D5', marginBottom: '12px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  cardTitulo: { fontSize: '15px', fontWeight: '600', color: '#2C1810', marginBottom: '4px' },
  cardDestinatario: { fontSize: '13px', color: '#8B6F47' },
  cardFecha: { fontSize: '12px', color: '#5C6B3A', marginTop: '4px', fontWeight: '500' },
  cardContenido: { fontSize: '14px', color: '#5C4033', lineHeight: '1.7', margin: 0, whiteSpace: 'pre-wrap' },
  btnEliminar: { backgroundColor: 'transparent', border: 'none', color: '#C4A882', fontSize: '16px', cursor: 'pointer', padding: '0 4px', flexShrink: 0 },
  archivoLink: { display: 'inline-block', marginTop: '10px', fontSize: '13px', color: '#5C6B3A', textDecoration: 'none', backgroundColor: '#F0F4E8', padding: '6px 12px', borderRadius: '6px', border: '1px solid #C8D5A0' },
}

export default Mensajes